import { useState, useEffect } from "react";
import { Plus, ArrowLeft, Search, MoveVertical } from "lucide-react";
import { sigteApiFetch, sigteApiFetchChecked } from "./sigteUtils";
import { Campo } from "./SigteFormField";

var BLANK = {interno:"",fabricante_num:"",marca_id:"",modelo:"",capacidad:"",velocidad:"",anio:"",tipo_id:"",estado_id:""};
// Colores de badge por estado: sigte-prototipo.jsx:329-334 (estadoEquipoEstilo).
// Ahi el estado es una constante fija; aqui viene de un catalogo editable
// (Fase 1), asi que cualquier valor que no matchee cae a un gris neutral en
// vez de tronar.
var ESTADO_BADGE = {
  "Activo": "bg-emerald-50 text-emerald-700",
  "Fuera de servicio": "bg-red-50 text-red-700",
  "Modernización": "bg-blue-50 text-blue-700",
  "Modernizacion": "bg-blue-50 text-blue-700",
  "Baja": "bg-neutral-100 text-neutral-500",
};
function estadoBadgeClass(label){ return ESTADO_BADGE[label] || "bg-neutral-100 text-neutral-500"; }

// Rediseno Bloque 3: sigte-prototipo.jsx:933-1142 (Equipos). El prototipo
// tiene lista -> detalle (ficha tecnica + historial) -> formulario; nuestro
// backend no tiene historial de equipo, asi que se queda en lista <->
// formulario, sin pagina de detalle -- no se inventa un historial que no
// existe. Se portea el buscador (linea 1109-1116) y la fila de lista con
// badge de estado por color (linea 1121-1133).
export function SigteEquipos(p) {
  var edificio = p.edificio;
  var [equipos,setEquipos]=useState([]);
  var [catalogos,setCatalogos]=useState([]);
  var [loading,setLoading]=useState(true);
  var [view,setView]=useState("list");
  var [editing,setEditing]=useState(null);
  var [form,setForm]=useState(BLANK);
  var [err,setErr]=useState("");
  var [confirmDeleteId,setConfirmDeleteId]=useState(null);
  var [busqueda,setBusqueda]=useState("");

  function load(){
    Promise.all([
      sigteApiFetch("/equipos?edificio_id="+edificio.id).then(function(r){return r.json();}),
      sigteApiFetch("/catalogos").then(function(r){return r.json();}),
    ]).then(function(res){
      setEquipos(res[0].equipos||[]);
      setCatalogos(res[1].catalogos||[]);
      setLoading(false);
    });
  }
  useEffect(load,[edificio.id]);

  function catOpts(categoria, currentId){
    return catalogos.filter(function(c){ return c.categoria===categoria && (c.activo || c.id===currentId); });
  }
  function catLabel(id){
    var c = catalogos.find(function(x){return x.id===id;});
    return c ? c.valor : "?";
  }

  function setF(k,v){ setForm(function(f){return Object.assign({},f,{[k]:v});}); }

  function startNew(){ setEditing(null); setForm(BLANK); setErr(""); setView("edit"); }
  function startEdit(eq){
    setEditing(eq);
    setForm(Object.assign({},BLANK,eq,{anio:eq.anio||""}));
    setErr(""); setView("edit");
  }

  async function save(){
    if(!form.marca_id||!form.tipo_id||!form.estado_id){setErr("Marca, tipo y estado son requeridos.");return;}
    var path = editing ? "/equipos/"+editing.id : "/equipos";
    var method = editing ? "PUT" : "POST";
    var body = Object.assign({},form,{
      marca_id: parseInt(form.marca_id,10),
      tipo_id: parseInt(form.tipo_id,10),
      estado_id: parseInt(form.estado_id,10),
      anio: form.anio ? parseInt(form.anio,10) : null,
    });
    if(!editing) body.edificio_id = edificio.id;
    try {
      await sigteApiFetchChecked(path,{method:method,body:JSON.stringify(body)});
      setView("list"); load();
    } catch(e) {
      setErr(e.message);
    }
  }

  async function del(eq){
    try {
      await sigteApiFetchChecked("/equipos/"+eq.id,{method:"DELETE"});
      setConfirmDeleteId(null); load();
    } catch(e) {
      setErr(e.message); setConfirmDeleteId(null);
    }
  }

  if(loading) return <p className="text-sm text-neutral-500">Cargando...</p>;

  var filtrados = equipos.filter(function(eq){
    var texto = ((eq.interno||"")+catLabel(eq.marca_id)+(eq.modelo||"")+catLabel(eq.tipo_id)).toLowerCase();
    return texto.includes(busqueda.toLowerCase());
  });

  return (
    <div className="space-y-6">
      <button onClick={p.onBack} className="inline-flex items-center gap-1 text-sm font-medium text-neutral-500 hover:text-neutral-900">
        <ArrowLeft size={16}/> Volver a Edificios
      </button>
      {view==="edit"?(
        <div className="space-y-6">
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">{editing?"Editar equipo":"Nuevo equipo"}</h1>
          <div className="rounded-2xl border border-neutral-200 bg-white p-5">
            {err?<div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>:null}
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo label="Número interno" value={form.interno} onChange={function(v){setF("interno",v);}} placeholder="ELE-A-01"/>
              <Campo label="Número de fabricante" value={form.fabricante_num} onChange={function(v){setF("fabricante_num",v);}} placeholder="OT-00000"/>
              <div>
                <label className="mb-1 block text-xs font-medium text-neutral-500">Marca</label>
                <select value={form.marca_id} onChange={function(e){setF("marca_id",e.target.value);}}
                  className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-neutral-400 focus:outline-none">
                  <option value="">Selecciona...</option>
                  {catOpts("marca",editing?editing.marca_id:null).map(function(c){return <option key={c.id} value={c.id}>{c.valor}</option>;})}
                </select>
              </div>
              <Campo label="Modelo" value={form.modelo} onChange={function(v){setF("modelo",v);}} placeholder="Gen2"/>
              <div>
                <label className="mb-1 block text-xs font-medium text-neutral-500">Tipo</label>
                <select value={form.tipo_id} onChange={function(e){setF("tipo_id",e.target.value);}}
                  className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-neutral-400 focus:outline-none">
                  <option value="">Selecciona...</option>
                  {catOpts("tipo",editing?editing.tipo_id:null).map(function(c){return <option key={c.id} value={c.id}>{c.valor}</option>;})}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-neutral-500">Estado</label>
                <select value={form.estado_id} onChange={function(e){setF("estado_id",e.target.value);}}
                  className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-neutral-400 focus:outline-none">
                  <option value="">Selecciona...</option>
                  {catOpts("estado",editing?editing.estado_id:null).map(function(c){return <option key={c.id} value={c.id}>{c.valor}</option>;})}
                </select>
              </div>
              <Campo label="Capacidad" value={form.capacidad} onChange={function(v){setF("capacidad",v);}} placeholder="1,000 kg / 13 personas"/>
              <Campo label="Velocidad" value={form.velocidad} onChange={function(v){setF("velocidad",v);}} placeholder="1.75 m/s"/>
              <Campo label="Año" type="number" value={form.anio} onChange={function(v){setF("anio",v);}} placeholder="2024"/>
            </div>
            <button onClick={save}
              className="mt-5 w-full rounded-xl bg-neutral-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-700 sm:w-auto">
              Guardar equipo
            </button>
          </div>
        </div>
      ):(
        <div className="space-y-6">
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">{"Equipos de "+edificio.nombre}</h1>
              <p className="mt-1 text-sm text-neutral-500">{equipos.length+" equipos"}</p>
            </div>
            <button onClick={startNew} className="inline-flex items-center gap-1.5 rounded-xl bg-neutral-900 px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-neutral-700">
              <Plus size={16}/> Nuevo equipo
            </button>
          </header>
          {err?<div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>:null}
          {equipos.length>0?(
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400"/>
              <input value={busqueda} onChange={function(e){setBusqueda(e.target.value);}} placeholder="Buscar por número, marca o tipo"
                className="w-full rounded-xl border border-neutral-200 bg-white py-2.5 pl-10 pr-4 text-sm text-neutral-900 placeholder-neutral-400 focus:border-neutral-400 focus:outline-none"/>
            </div>
          ):null}
          <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
            {filtrados.map(function(eq,i){
              var estadoLabel = catLabel(eq.estado_id);
              return (
                <div key={eq.id} className={"flex flex-wrap items-center justify-between gap-3 px-5 py-4 " + (i>0 ? "border-t border-neutral-100" : "")}>
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-neutral-100 text-neutral-400">
                      <MoveVertical size={17}/>
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-neutral-900">{(eq.interno||"Sin interno")+" · "+catLabel(eq.marca_id)+" "+(eq.modelo||"")}</p>
                      <p className="mt-0.5 truncate text-xs text-neutral-500">{catLabel(eq.tipo_id)}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={"rounded-full px-2.5 py-0.5 text-xs font-medium " + estadoBadgeClass(estadoLabel)}>{estadoLabel}</span>
                    <button onClick={function(){startEdit(eq);}} className="rounded-xl border border-neutral-200 px-3.5 py-2 text-sm font-medium text-neutral-700 transition-colors hover:border-neutral-400">Editar</button>
                    {confirmDeleteId===eq.id?(
                      <div className="flex items-center gap-2 rounded-xl bg-red-50 px-3 py-2">
                        <span className="text-xs text-red-700">¿Eliminar?</span>
                        <button onClick={function(){del(eq);}} className="text-xs font-medium text-red-700 underline">Sí</button>
                        <button onClick={function(){setConfirmDeleteId(null);}} className="text-xs font-medium text-neutral-500">No</button>
                      </div>
                    ):(
                      <button onClick={function(){setConfirmDeleteId(eq.id);}} className="rounded-xl border border-red-200 px-3.5 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50">Eliminar</button>
                    )}
                  </div>
                </div>
              );
            })}
            {filtrados.length===0?<p className="px-5 py-8 text-center text-sm text-neutral-400">{equipos.length===0?"No hay equipos registrados.":"Ningún equipo coincide con la búsqueda."}</p>:null}
          </div>
        </div>
      )}
    </div>
  );
}
